/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ChatParticipant,
  ChatParticipant$inboundSchema,
} from "./chatparticipant.js";

export type ChatOverview = {
  /**
   * Email of the AI account that produced the chat, resolved from the linked AI account. May differ from the employee's work email (e.g. a personal account).
   */
  accountEmail?: string | undefined;
  /**
   * Account type that produced the chat ('team', 'personal', or empty), resolved from the linked AI account.
   */
  accountType?: string | undefined;
  /**
   * The ID of the agent the assistant acts as, when the assistant has a dedicated agent identity
   */
  assistantAgentId?: string | undefined;
  /**
   * The ID of the assistant that produced this chat, if any
   */
  assistantId?: string | undefined;
  /**
   * The name of the assistant that produced this chat, if any
   */
  assistantName?: string | undefined;
  /**
   * When the chat was created.
   */
  createdAt: Date;
  /**
   * The ID of the external user who created the chat
   */
  externalUserId?: string | undefined;
  /**
   * The ID of the chat
   */
  id: string;
  /**
   * When the last message in the chat was created.
   */
  lastMessageTimestamp: Date;
  /**
   * True when the session's traffic was observed by the LiteLLM proxy, including sessions whose transcript is owned by the agent's own hook stream
   */
  litellmProxied?: boolean | undefined;
  /**
   * The number of messages in the chat
   */
  numMessages: number;
  /**
   * The supported client that originated a chat routed through the source, when known
   */
  originatingClient?: string | undefined;
  /**
   * Distinct observed conversation participants across the session.
   */
  participants?: Array<ChatParticipant> | undefined;
  /**
   * True when the chat is pinned
   */
  pinned?: boolean | undefined;
  /**
   * Number of risk findings recorded against messages in this chat (project-scoped, found=true). Only populated by endpoints that join risk data; absent elsewhere.
   */
  riskFindingsCount?: number | undefined;
  /**
   * Observed Slack channel associated with this session.
   */
  slackChannelId?: string | undefined;
  /**
   * Observed Slack channel name as reported by the captured envelope.
   */
  slackChannelName?: string | undefined;
  /**
   * Observed Slack workspace associated with this session.
   */
  slackTeamId?: string | undefined;
  /**
   * The source of the chat: Elements, Playground, ClaudeCode (inferred from messages)
   */
  source?: string | undefined;
  /**
   * Persisted LLM summary of the session transcript, if one has been generated
   */
  summary?: string | undefined;
  /**
   * When the session summary was last generated.
   */
  summaryGeneratedAt?: Date | undefined;
  /**
   * The title of the chat
   */
  title: string;
  /**
   * Total cost in USD for this chat
   */
  totalCost?: number | undefined;
  /**
   * Total input tokens used in this chat
   */
  totalInputTokens?: number | undefined;
  /**
   * Total output tokens used in this chat
   */
  totalOutputTokens?: number | undefined;
  /**
   * Total tokens (input + output) used in this chat
   */
  totalTokens?: number | undefined;
  /**
   * When the chat was last updated.
   */
  updatedAt: Date;
  /**
   * The ID of the user who created the chat
   */
  userId?: string | undefined;
  /**
   * Work units of value delivered in this chat as judged by the work-units analysis. Absent unless the organization has work-units analysis enabled and this chat has been scored.
   */
  workUnits?: number | undefined;
};

/** @internal */
export const ChatOverview$inboundSchema: z.ZodMiniType<ChatOverview, unknown> =
  z.pipe(
    z.object({
      account_email: z.optional(z.string()),
      account_type: z.optional(z.string()),
      assistant_agent_id: z.optional(z.string()),
      assistant_id: z.optional(z.string()),
      assistant_name: z.optional(z.string()),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      external_user_id: z.optional(z.string()),
      id: z.string(),
      last_message_timestamp: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      litellm_proxied: z.optional(z.boolean()),
      num_messages: z.int(),
      originating_client: z.optional(z.string()),
      participants: z.optional(z.array(ChatParticipant$inboundSchema)),
      pinned: z.optional(z.boolean()),
      risk_findings_count: z.optional(z.int()),
      slack_channel_id: z.optional(z.string()),
      slack_channel_name: z.optional(z.string()),
      slack_team_id: z.optional(z.string()),
      source: z.optional(z.string()),
      summary: z.optional(z.string()),
      summary_generated_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      title: z.string(),
      total_cost: z.optional(z.number()),
      total_input_tokens: z.optional(z.int()),
      total_output_tokens: z.optional(z.int()),
      total_tokens: z.optional(z.int()),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      user_id: z.optional(z.string()),
      work_units: z.optional(z.number()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "account_email": "accountEmail",
        "account_type": "accountType",
        "assistant_agent_id": "assistantAgentId",
        "assistant_id": "assistantId",
        "assistant_name": "assistantName",
        "created_at": "createdAt",
        "external_user_id": "externalUserId",
        "last_message_timestamp": "lastMessageTimestamp",
        "litellm_proxied": "litellmProxied",
        "num_messages": "numMessages",
        "originating_client": "originatingClient",
        "risk_findings_count": "riskFindingsCount",
        "slack_channel_id": "slackChannelId",
        "slack_channel_name": "slackChannelName",
        "slack_team_id": "slackTeamId",
        "summary_generated_at": "summaryGeneratedAt",
        "total_cost": "totalCost",
        "total_input_tokens": "totalInputTokens",
        "total_output_tokens": "totalOutputTokens",
        "total_tokens": "totalTokens",
        "updated_at": "updatedAt",
        "user_id": "userId",
        "work_units": "workUnits",
      });
    }),
  );

export function chatOverviewFromJSON(
  jsonString: string,
): SafeParseResult<ChatOverview, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ChatOverview$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ChatOverview' from JSON`,
  );
}
